import { useReadContracts } from 'wagmi';
import {
  erc20Abi,
  minterRegistryAbi,
  syntraTokenAbi,
  priceOracleAbi,
  sSyntraAbi,
  stakingManagerAbi,
  uniswapV2PairAbi,
} from '@syntra/abi';
import { DEPLOYMENT as D } from '../config/env.ts';
import { E18 } from '../lib/format.ts';
import { effectiveRates } from '../lib/plans.ts';

const contracts = [
  { address: D.oracle, abi: priceOracleAbi, functionName: 'syntraPriceUsd' },
  { address: D.oracle, abi: priceOracleAbi, functionName: 'syntraSpotPriceUsd' },
  { address: D.oracle, abi: priceOracleAbi, functionName: 'bnbPriceUsd' },
  { address: D.oracle, abi: priceOracleAbi, functionName: 'lpPriceUsd' },
  { address: D.syntra, abi: syntraTokenAbi, functionName: 'totalSupply' },
  { address: D.staking, abi: stakingManagerAbi, functionName: 'maxRate' },
  { address: D.sSyntra, abi: sSyntraAbi, functionName: 'totalSupply' },
  { address: D.staking, abi: stakingManagerAbi, functionName: 'fees' },
  { address: D.staking, abi: stakingManagerAbi, functionName: 'plan', args: [0] },
  { address: D.staking, abi: stakingManagerAbi, functionName: 'plan', args: [1] },
  { address: D.staking, abi: stakingManagerAbi, functionName: 'nextEpochAt' },
  { address: D.staking, abi: stakingManagerAbi, functionName: 'plan', args: [2] },
  { address: D.staking, abi: stakingManagerAbi, functionName: 'plan', args: [3] },
  { address: D.staking, abi: stakingManagerAbi, functionName: 'plan', args: [4] },
  { address: D.usdt, abi: erc20Abi, functionName: 'balanceOf', args: [D.treasury] },
  { address: D.wbnb, abi: erc20Abi, functionName: 'balanceOf', args: [D.treasury] },
  { address: D.syntraUsdtPair, abi: erc20Abi, functionName: 'balanceOf', args: [D.treasury] },
  { address: D.syntraUsdtPair, abi: uniswapV2PairAbi, functionName: 'getReserves' },
  { address: D.syntraUsdtPair, abi: uniswapV2PairAbi, functionName: 'token0' },
  { address: D.staking, abi: stakingManagerAbi, functionName: 'currentEpoch' },
  { address: D.staking, abi: stakingManagerAbi, functionName: 'plan', args: [5] },
  { address: D.registry, abi: minterRegistryAbi, functionName: 'minterConfig', args: [D.staking] },
  { address: D.registry, abi: minterRegistryAbi, functionName: 'available', args: [D.staking] },
] as const;

/** The six staking plans: 0 no lock (the SYN ↔ sSYN conversion), 1–4 30/90/180/365-day locks, 5 permanent. */
export const PLAN_IDS = [0, 1, 2, 3, 4, 5] as const;
export type PlanId = (typeof PLAN_IDS)[number];
export const NO_LOCK: PlanId = 0;
export const PERMANENT: PlanId = 5;
/** The timed locks, shortest first: every plan between the no-lock and the permanent plan. */
export const TIMED_IDS = PLAN_IDS.filter((id) => id !== NO_LOCK && id !== PERMANENT);
export const LONGEST_LOCK: PlanId = 4;
export const isTimed = (id: number) => id > NO_LOCK && id < PERMANENT;

export interface PlanView {
  id: PlanId;
  rate: bigint; // per epoch, 1e18: the plan's full ("up to") rate
  /** What the plan pays per epoch at today's staked value: `rate`, or less once the mint budget binds. */
  paidRate: bigint;
  lockDuration: number; // seconds (0: none / permanent)
  permanent: boolean;
  index: bigint;
  totalValue: bigint;
  stakeFeeBps: bigint;
  unstakeFeeBps: bigint;
}

export interface Protocol {
  price?: bigint;
  spot?: bigint;
  bnbPrice?: bigint;
  lpPrice?: bigint;
  supply?: bigint;
  plans?: PlanView[];
  /** The staking mint budget: what the plans want per epoch against what the registry refills. */
  budget?: { cap: bigint; available: bigint; wanted: bigint; capped: boolean; factor: number };
  maxRate?: bigint;
  /** All SYN staked across the plans (sSYN total supply). */
  totalStaked?: bigint;
  nextEpochAt?: number;
  epoch?: number;
  treasuryUsd?: bigint;
  marketCap?: bigint;
  backingPerToken?: bigint;
  stakedShare?: number;
  runwayDays?: number;
  poolSyntra?: bigint;
  poolUsdt?: bigint;
}

/** Everything the dashboard, ticker and forms need about the protocol, in one multicall every 15 s. */
export function useProtocol(): { data: Protocol; isLoading: boolean } {
  const q = useReadContracts({ contracts, allowFailure: true, query: { refetchInterval: 15_000 } });
  const r = q.data;
  const val = <T,>(i: number) => (r?.[i]?.status === 'success' ? (r[i].result as T) : undefined);
  const price = val<bigint>(0);
  const supply = val<bigint>(4);
  const totalStaked = val<bigint>(6);
  const fees = val<readonly [readonly bigint[], readonly bigint[]]>(7);
  type RawPlan = { rate: bigint; lockDuration: bigint; permanent: boolean; index: bigint; totalValue: bigint };
  // the multicall positions of plans 0–5 (plan 5 was added at the end so the other positions kept their place)
  const raw = [8, 9, 11, 12, 13, 20].map((i) => val<RawPlan>(i));
  const minter = val<{ capPerEpoch: bigint; burstEpochs: number; enabled: boolean }>(21);
  const eff = effectiveRates(raw.every(Boolean) ? raw.map((r) => ({ rate: r!.rate, totalValue: r!.totalValue })) : undefined, minter?.capPerEpoch);
  const plans: PlanView[] | undefined =
    fees && raw.every(Boolean)
      ? raw.map((r, id) => ({
          id: id as PlanId,
          rate: r!.rate,
          paidRate: eff.rates[id] ?? r!.rate,
          lockDuration: Number(r!.lockDuration),
          permanent: r!.permanent,
          index: r!.index,
          totalValue: r!.totalValue,
          stakeFeeBps: fees[0][id],
          unstakeFeeBps: fees[1][id],
        }))
      : undefined;
  const budget = minter ? { cap: minter.capPerEpoch, available: val<bigint>(22) ?? 0n, wanted: eff.wanted, capped: eff.capped, factor: eff.factor } : undefined;
  const bnbPrice = val<bigint>(2);
  const lpPrice = val<bigint>(3);
  const usdtBal = val<bigint>(14);
  const wbnbBal = val<bigint>(15);
  const lpBal = val<bigint>(16);
  const reserves = val<readonly [bigint, bigint, number]>(17);
  const token0 = val<string>(18);

  // risk-free backing: USDT + BNB + protocol-owned LP (SYN held by the treasury is not counted)
  let treasuryUsd: bigint | undefined;
  if (usdtBal !== undefined && wbnbBal !== undefined && lpBal !== undefined) {
    treasuryUsd = usdtBal + (bnbPrice ? (wbnbBal * bnbPrice) / E18 : 0n) + (lpPrice ? (lpBal * lpPrice) / E18 : 0n);
  }
  const syntraIs0 = token0?.toLowerCase() === D.syntra.toLowerCase();
  const data: Protocol = {
    price,
    spot: val<bigint>(1),
    bnbPrice,
    lpPrice,
    supply,
    plans,
    budget,
    maxRate: val<bigint>(5),
    totalStaked,
    nextEpochAt: val<bigint>(10) !== undefined ? Number(val<bigint>(10)) : undefined,
    epoch: val<bigint>(19) !== undefined ? Number(val<bigint>(19)) : undefined,
    treasuryUsd,
    marketCap: price !== undefined && supply !== undefined ? (supply * price) / E18 : undefined,
    backingPerToken: treasuryUsd !== undefined && supply ? (treasuryUsd * E18) / supply : undefined,
    stakedShare: supply && totalStaked !== undefined ? Number((totalStaked * 10_000n) / supply) / 10_000 : undefined,
    poolSyntra: reserves ? (syntraIs0 ? reserves[0] : reserves[1]) : undefined,
    poolUsdt: reserves ? (syntraIs0 ? reserves[1] : reserves[0]) : undefined,
  };
  if (treasuryUsd !== undefined && price && plans) {
    const dailyMint = (2n * plans.reduce((s, pl) => s + pl.totalValue * pl.rate, 0n)) / E18;
    const dailyUsd = (dailyMint * price) / E18;
    data.runwayDays = dailyUsd > 0n ? Number((treasuryUsd * 100n) / dailyUsd) / 100 : Infinity;
  }
  return { data, isLoading: q.isLoading };
}
